import { Suspense, lazy, useContext, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams } from "react-router-dom";

import { ProcedureContext } from "@/context/ProcedureContext";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";

import { Translate } from "@/components/Application/Translate";
import { tokens } from "@/components/Application/theme";
import { useEditorContent } from "@/components/Procedures/API/ChecklistQueries";
import ManualChecklistEditorActions from "@/components/Procedures/Components/Checklist/EditorActions/ManualChecklistEditorActions";

import ComponentError from "@/helpers/components/Errors/Components";
import Spinner from "@/helpers/components/Spinner";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

import {
	UpsertProcedureNodeSteps,
	useDeleteChecklist,
} from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

const CreateStep = lazy(
	() => import("@/components/Procedures/Components/CreateStep/CreateStep"),
);
const EditChecklistInfo = lazy(
	() =>
		import("@/components/Procedures/Components/CreateStep/EditChecklistInfo"),
);

const ManualChecklistEditorContainer = () => {
	const { id, schemeId } = useParams<RouteParams>();
	const checklistId = +schemeId!;

	const { activeStep } = useContext(ProcedureContext);

	const { data: editorContent } = useEditorContent(checklistId);

	return (
		<ErrorBoundary
			fallbackRender={({ error, resetErrorBoundary }) => (
				<ComponentError
					error={error.message}
					resetErrorBoundary={resetErrorBoundary}
				/>
			)}
		>
			<Suspense fallback={<Spinner text="" />}>
				<Box position="relative">
					<>
						{activeStep === -1 ? (
							<EditChecklistInfo activeChecklistId={checklistId} />
						) : (
							<CreateStep
								index={activeStep}
								step={editorContent?.Steps[activeStep]}
								activeChecklistId={0}
							/>
						)}
						<ManualChecklistEditorActions
							sx={{ position: "sticky", top: 50 }}
							activeChecklistId={checklistId}
						/>
					</>
				</Box>
			</Suspense>
		</ErrorBoundary>
	);
};

export default ManualChecklistEditorContainer;
